import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Accordion, Box, Button, FormControl, MenuItem, TextField } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ReminderForm from 'common/components/ReminderForm';
import RoundedSelect from 'common/components/RoundedSelect';
import ServiceIssuesReportFrequency from 'common/enums/serviceIssuesReportFrequency';
import { isValidHttpUrl } from 'common/validators/validators';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { fetchInstallationsAction, getInstallations } from 'redux/slices/installationSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import { getSettingsDirty } from 'redux/slices/profileSlice';
import { fetchTriagesAction, getTriages } from 'redux/slices/triageSlice';
import JsonCodeInputMonacoEditor from '../../../../common/components/code-editor/JsonCodeInputMonacoEditor';
import SimpleCodeErrorView from '../../../../common/components/code-editor/SimpleCodeErrorView';
import ItemSettings from '../components/ItemSettings';
import useStylesFieldForm from '../styles/useStylesFieldForm.styles';
import useStylesSettings from '../styles/useStylesSettings.styles';
import ICalendarSettingsV3 from './ICalendarSettingsV3';
import ICalendarSettingsV4 from './ICalendarSettingsV4';
import ICalendarReminder from './components/ICalendarReminder';

const ICalendarSettings = (props) => {
    const {
        toggleChange,
        handleSubmit,
        hasChanges,
        serviceAppointmentCreateTriageId,
        appointmentRatingInstallation,
        appointmentInstallationUrl,
        defaultContextType,
        sendBookingEmailToAgent,
        appointmentStatus,
        appointmentOrigin,
        enableAgentEditAvailability,
        icalendarEmailPreviousReminders,
        icalendarEmailPastReminders,
        enableAgentEditGroupAppointment,
        icalendarIntegration,
        calendarVersion,
        bookingTitle,
        enableContactRequests,
        enableLostOpportunities,
        enableSaveServiceWithIssues,
        serviceIssuesReportFrequency,
    } = props;

    const { t } = useTranslation();
    const { classes: classes1 } = useStylesFieldForm();
    const { classes: classes2, cx } = useStylesSettings();
    const classes = { ...classes1, ...classes2 };
    const triages = useSelector(getTriages);
    const settingsDirty = useSelector(getSettingsDirty);
    const user = useSelector(getUser);
    const organization = useSelector(getOrganization);
    const { id: organizationId } = organization;
    const dispatch = useDispatch();
    const [inputError, setInputError] = useState({ appointmentStatus: null, appointmentOrigin: null });
    const installations = useSelector(getInstallations);
    const [selectedVersion, setSelectedVersion] = useState(calendarVersion);
    const [originalVersion, setOriginalVersion] = useState(calendarVersion);

    // Load triages and installations and calendar integration data
    // when the component mounts
    const loadData = useCallback(() => {
        dispatch(
            fetchTriagesAction({
                organizationId,
            }),
        );
        dispatch(
            fetchInstallationsAction({
                organizationId,
            }),
        );
    }, [dispatch, organizationId]);

    useEffect(() => {
        setOriginalVersion(calendarVersion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasVersionChanged = useMemo(() => {
        return originalVersion !== selectedVersion;
    }, [originalVersion, selectedVersion]);

    const handleCheckChange = (event, settingName) => {
        toggleChange(settingName, event.target.checked);
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        if (name === SettingsKey.CALENDAR_VERSION) {
            setSelectedVersion(value);
        }
        toggleChange(name, value);
    };

    const validInstallationUrl = (url) => {
        return isValidHttpUrl(url) || url === '' || !url;
    };

    if (user?.role !== 'root' && (user?.role !== 'admin' || !icalendarIntegration)) return null;

    const renderVersionSettings = () => {
        const currentVersion = selectedVersion || calendarVersion;

        const commonProps = {
            toggleChange,
            organizationId,
            isMigrating: hasVersionChanged,
        };
        if (currentVersion === 3) {
            return <ICalendarSettingsV3 {...commonProps} icalendarIntegration={icalendarIntegration} />;
        }

        if (currentVersion === 4) {
            return <ICalendarSettingsV4 {...commonProps} />;
        }

        return null;
    };

    return (
        <Accordion className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ArrowRightIcon className={classes.accordionIcon} />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Typography variant="subtitle1" fontWeight={500}>
                    {t('profile.settings.iCalendar')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.eventSection}>
                <div
                    className={classes.pb20px}
                    style={{ display: settingsDirty && hasChanges ? 'inline-flex' : 'none' }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <Button variant="contained" className={classes.redColor} size="small" onClick={handleSubmit}>
                            {t('Save changes')}
                        </Button>
                    </Box>
                </div>

                {user?.role === 'root' && (
                    <section>
                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>Calendar Version</Typography>
                            <RoundedSelect
                                value={+calendarVersion}
                                fullWidth
                                name={SettingsKey.CALENDAR_VERSION}
                                onChange={(e) => handleSelectChange(e)}
                                iconStyle={{ fontSize: '13px', padding: '0 12px', color: 'darkslategrey' }}
                            >
                                {[3, 4]?.map((version) => (
                                    <MenuItem value={version} key={version} selected={version === +calendarVersion}>
                                        {version}
                                    </MenuItem>
                                ))}
                            </RoundedSelect>

                            {hasVersionChanged && (
                                <Typography
                                    variant="overline"
                                    color="warning.main"
                                    display="block"
                                    style={{
                                        marginTop: '8px',
                                        backgroundColor: '#fff3e0',
                                        padding: '8px',
                                        borderRadius: '4px',
                                    }}
                                >
                                    {t('profile.settings.iCalendar.versionChangeWarning', {
                                        fromVersion: originalVersion,
                                        toVersion: selectedVersion,
                                    })}
                                </Typography>
                            )}
                        </div>
                    </section>
                )}

                <>{renderVersionSettings()}</>

                {user?.role === 'root' && (
                    <section>
                        <ItemSettings
                            label={t('profile.settings.iCalendar.sendBookingEmailToAgent')}
                            onChange={handleCheckChange}
                            name={SettingsKey.SEND_BOOKING_EMAIL_TO_AGENT}
                            keySettings={SettingsKey.SEND_BOOKING_EMAIL_TO_AGENT}
                            value={sendBookingEmailToAgent}
                            paddingBottom
                        />

                        <ItemSettings
                            label={t('profile.settings.iCalendar.enableAgentEditAvailability')}
                            onChange={handleCheckChange}
                            name={'enableAgentEditAvailability'}
                            keySettings={SettingsKey.ENABLE_AGENT_EDIT_AVAILABILITY}
                            value={enableAgentEditAvailability}
                            paddingBottom
                        />

                        <ItemSettings
                            label={t('profile.settings.iCalendar.enableAgentEditGroupAppointment')}
                            onChange={handleCheckChange}
                            name={'enableAgentEditGroupAppointment'}
                            keySettings={SettingsKey.ENABLE_AGENT_EDIT_GROUP_APPOINTMENT}
                            value={enableAgentEditGroupAppointment}
                            paddingBottom
                        />

                        <ItemSettings
                            label={t('profile.settings.iCalendar.enableContactRequests')}
                            onChange={handleCheckChange}
                            name={'enableContactRequests'}
                            keySettings={SettingsKey.ICALENDAR_CONTACT_REQUESTS}
                            value={enableContactRequests}
                            paddingBottom
                        />

                        <ItemSettings
                            label={t('profile.settings.iCalendar.enableLostOpportunities')}
                            onChange={handleCheckChange}
                            name={'enableLostOpportunities'}
                            keySettings={SettingsKey.ICALENDAR_LOST_OPPORTUNITIES}
                            value={enableLostOpportunities}
                            paddingBottom
                        />

                        <ItemSettings
                            label={t('profile.settings.iCalendar.enableSaveServiceWithIssues')}
                            onChange={handleCheckChange}
                            name={'enableSaveServiceWithIssues'}
                            keySettings={SettingsKey.ENABLE_SAVE_SERVICE_WITH_ISSUES}
                            value={enableSaveServiceWithIssues}
                            paddingBottom
                        />

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.iCalendar.serviceIssuesReportFrequency')}
                            </Typography>
                            <RoundedSelect
                                value={serviceIssuesReportFrequency || ServiceIssuesReportFrequency.NEVER}
                                fullWidth
                                name={SettingsKey.SERVICE_ISSUES_REPORT_FREQUENCY}
                                onChange={(e) => handleSelectChange(e)}
                                iconStyle={{ fontSize: '13px', padding: '0 12px', color: 'darkslategrey' }}
                            >
                                {Object.values(ServiceIssuesReportFrequency).map((frequency) => (
                                    <MenuItem
                                        value={frequency}
                                        key={frequency}
                                        selected={frequency === serviceIssuesReportFrequency}
                                    >
                                        {t(`profile.settings.iCalendar.serviceIssuesReportFrequency.${frequency}`)}
                                    </MenuItem>
                                ))}
                            </RoundedSelect>
                        </div>

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.iCalendar.triageAppointmentCreate')}
                            </Typography>
                            <RoundedSelect
                                value={serviceAppointmentCreateTriageId}
                                fullWidth
                                name={SettingsKey.SERVICE_APPOINTMENT_CREATE_TRIAGE_ID}
                                onChange={(e) => handleSelectChange(e)}
                                iconStyle={{ fontSize: '13px', padding: '0 12px', color: 'darkslategrey' }}
                            >
                                {triages?.map((triage) => (
                                    <MenuItem
                                        value={triage._id}
                                        key={triage._id}
                                        selected={triage._id === serviceAppointmentCreateTriageId}
                                    >
                                        {triage.name}
                                    </MenuItem>
                                ))}
                            </RoundedSelect>
                        </div>

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.iCalendar.triageAppointmentRating')}
                            </Typography>
                            <RoundedSelect
                                value={appointmentRatingInstallation}
                                fullWidth
                                name={SettingsKey.APPOINTMENT_RATING_INSTALLATION}
                                onChange={(e) => handleSelectChange(e)}
                                iconStyle={{ fontSize: '13px', padding: '0 12px', color: 'darkslategrey' }}
                            >
                                {installations?.map((i) => (
                                    <MenuItem value={i.id} key={i.id} selected={i.id === appointmentRatingInstallation}>
                                        {i.name}
                                    </MenuItem>
                                ))}
                            </RoundedSelect>
                        </div>

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.calendar.bookingTitle')}
                            </Typography>
                            <FormControl fullWidth>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    type="text"
                                    value={bookingTitle || ''}
                                    onChange={(e) => {
                                        const {
                                            target: { value },
                                        } = e;
                                        handleSelectChange({
                                            target: { name: SettingsKey.BOOKING_TITLE, value },
                                        });
                                    }}
                                    placeholder={t('profile.settings.calendar.bookingTitlePlaceholder')}
                                />
                            </FormControl>
                        </div>

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.iCalendar.appointmentInstallationUrl')}
                            </Typography>
                            <FormControl fullWidth>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    type="url"
                                    value={appointmentInstallationUrl || ''}
                                    onChange={(e) => {
                                        const {
                                            target: { value },
                                        } = e;
                                        handleSelectChange({
                                            target: { name: SettingsKey.APPOINTMENT_INSTALLATION_URL, value },
                                        });
                                    }}
                                    placeholder="https://app.onlive.site"
                                    error={!validInstallationUrl(appointmentInstallationUrl)}
                                    helperText={
                                        !validInstallationUrl(appointmentInstallationUrl)
                                            ? t('profile.settings.urlValidationError')
                                            : ''
                                    }
                                />
                            </FormControl>
                        </div>

                        <div className={cx(classes.pt20px, classes.pb20px)}>
                            <Typography className={classes.emailsTemplatesText}>
                                {t('profile.settings.iCalendar.defaultContextType')}
                            </Typography>
                            <FormControl fullWidth>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    type="text"
                                    value={defaultContextType || ''}
                                    onChange={(e) => {
                                        const {
                                            target: { value },
                                        } = e;
                                        handleSelectChange({
                                            target: { name: SettingsKey.APPOINTMENT_DEFAULT_CONTEXT_TYPE, value },
                                        });
                                    }}
                                    placeholder="TestDrive"
                                />
                            </FormControl>
                        </div>

                        <div className={classes.pb20px}>
                            <Typography className={classes.emailsTemplatesText}>{`${t(
                                'profile.settings.iCalendar.appointmentStatus',
                            )}`}</Typography>
                            <JsonCodeInputMonacoEditor
                                name="appointmentStatus"
                                value={appointmentStatus}
                                onChange={(value, errors) => {
                                    const codeHaveErrors = errors && Array.isArray(errors) && errors.length > 0;
                                    const newErrors = codeHaveErrors ? errors : null;
                                    setInputError({ appointmentStatus: newErrors });
                                    toggleChange(SettingsKey.APPOINTMENT_STATUS, value);
                                }}
                            />
                            <SimpleCodeErrorView
                                id={'error-context'}
                                mainErrorMessage={t('JsonError')}
                                errorList={inputError['appointmentStatus']}
                            />
                        </div>

                        <div className={classes.pb20px}>
                            <Typography className={classes.emailsTemplatesText}>{`${t(
                                'profile.settings.iCalendar.appointmentOrigin',
                            )}`}</Typography>
                            <JsonCodeInputMonacoEditor
                                name="appointmentOrigin"
                                value={appointmentOrigin}
                                onChange={(value, errors) => {
                                    const codeHaveErrors = errors && Array.isArray(errors) && errors.length > 0;
                                    const newErrors = codeHaveErrors ? errors : null;
                                    setInputError({ appointmentOrigin: newErrors });
                                    toggleChange(SettingsKey.APPOINTMENT_ORIGIN, value);
                                }}
                            />
                            <SimpleCodeErrorView
                                id={'error-context'}
                                mainErrorMessage={t('JsonError')}
                                errorList={inputError['appointmentOrigin']}
                            />
                        </div>

                        {calendarVersion === 3 && (
                            <>
                                <ReminderForm
                                    emailReminders={icalendarEmailPreviousReminders}
                                    toggleChange={toggleChange}
                                    settingKey={SettingsKey.ICALL_EMAIL_REMINDERS}
                                    time="before"
                                />

                                <ReminderForm
                                    emailReminders={icalendarEmailPastReminders}
                                    toggleChange={toggleChange}
                                    settingKey={SettingsKey.ICALENDAR_EMAIL_PAST_REMINDERS}
                                    time="after"
                                />
                            </>
                        )}

                        {calendarVersion === 3 && (
                            <>
                                <ReminderForm
                                    emailReminders={icalendarEmailPreviousReminders}
                                    toggleChange={toggleChange}
                                    settingKey={SettingsKey.ICALL_EMAIL_REMINDERS}
                                    time="before"
                                />

                                <ReminderForm
                                    emailReminders={icalendarEmailPastReminders}
                                    toggleChange={toggleChange}
                                    settingKey={SettingsKey.ICALENDAR_EMAIL_PAST_REMINDERS}
                                    time="after"
                                />
                            </>
                        )}
                        {calendarVersion === 4 && <ICalendarReminder />}
                    </section>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default ICalendarSettings;
